import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import ApproveRequestDialogIcon from "@entrilia/shared/icons/ApproveRequestDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";

interface Props {
  workflowId: string;
  approvalId: string | undefined;
  open: boolean;
  onClose: () => void;
  onApproved: () => void;
}

const approveRequest = withErrorHandling(adminApi.approveInvestorChangeRequest);
const approveApproval = withErrorHandling(adminApi.approveApproval);

const ApprovalConfirmationDialog = ({ workflowId, approvalId, open, onClose, onApproved }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [loading, setLoading] = useState(false);

  const handleApproveClick = async () => {
    setLoading(true);

    let error: ApiError | undefined;
    if (approvalId) {
      const [, requestError] = await approveApproval(approvalId, {});
      error = requestError;
    } else {
      const [, requestError] = await approveRequest({ workflowId });
      error = requestError;
    }

    setLoading(false);

    if (error) {
      logError(error, "[ApprovalConfirmationDialog]");
      sendNotificationError(error.message);
      return;
    }

    onClose();
    sendNotification("The request was approved");
    onApproved();
  };

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Approve</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <ApproveRequestDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Approve request?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              The changes will be applied to the system immediately after approval.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" loading={loading} onClick={handleApproveClick}>
          Approve
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default ApprovalConfirmationDialog;
